import { create } from 'zustand';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  BrandHubApi,
  BatchSyncHistoryRequest,
  BatchSyncStatusResponse,
} from 'app/modules/brandHub/api/brandHub';
import Logger from 'utils/logger';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import TimeUtils from 'app/utils/time';

const MAX_POLL_DURATION = TimeUtils.calcMilliseconds.minutes(5);
const POLL_INTERVAL = TimeUtils.calcMilliseconds.seconds(3);

type SyncBatchState = {
  batchId: string | null;
  startedAt: number | null;
  setBatchId: (batchId: string | null) => void;
  reset: () => void;
  isPollingActive: () => boolean;
  hasExceededMaxDuration: () => boolean;
};

const useSyncBatchState = create<SyncBatchState>((set, get) => ({
  batchId: null,
  startedAt: null,

  setBatchId: (batchId: string | null) => {
    if (batchId) {
      set({ batchId, startedAt: Date.now() });
      Logger.info('[SyncBatch] Started polling', { batchId });
    } else {
      const state = get();
      if (state.batchId) {
        Logger.info('[SyncBatch] Stopped polling', { batchId: state.batchId });
      }
      set({ batchId: null, startedAt: null });
    }
  },

  reset: () => {
    const state = get();
    if (state.batchId) {
      Logger.info('[SyncBatch] Reset polling', { batchId: state.batchId });
    }
    set({ batchId: null, startedAt: null });
  },

  isPollingActive: () => {
    const state = get();
    return state.batchId !== null && !state.hasExceededMaxDuration();
  },

  hasExceededMaxDuration: () => {
    const state = get();
    if (!state.startedAt) return false;
    return Date.now() - state.startedAt > MAX_POLL_DURATION;
  },
}));

export const useSyncHistoryBatch = () => {
  const queryClient = useQueryClient();
  const {
    batchId,
    setBatchId,
    reset,
    isPollingActive,
    hasExceededMaxDuration,
  } = useSyncBatchState();

  const triggerSync = useMutation(
    (params: BatchSyncHistoryRequest) => BrandHubApi.syncHistoryBatch(params),
    {
      onSuccess: (response) => {
        Logger.info('[SyncBatch] Sync triggered', response);
        setBatchId(response.batchId);
      },
      onError: (error: ApiClientError) => {
        Logger.error('[SyncBatch] Failed to trigger sync', error);
      },
    },
  );

  const statusQuery = useQuery(
    queryKey.brandHubSyncBatchStatus(batchId),
    () => BrandHubApi.getBatchSyncStatus(batchId!),
    {
      enabled: !!batchId && isPollingActive(),
      refetchInterval: (data: BatchSyncStatusResponse | undefined) => {
        if (hasExceededMaxDuration()) {
          Logger.warn('[SyncBatch] Max poll duration exceeded', { batchId });
          reset();
          return false;
        }

        if (!data || data.status === 'in_progress') {
          return POLL_INTERVAL;
        }

        return false;
      },
      onSuccess: (data) => {
        if (data.status === 'completed' || data.status === 'partial') {
          Logger.info('[SyncBatch] Sync completed', {
            batchId: data.batchId,
            completedSyncs: data.completedSyncs,
            totalSyncs: data.totalSyncs,
          });

          queryClient.invalidateQueries(queryKey.posts());
          reset();
        } else if (data.status === 'failed') {
          Logger.error('[SyncBatch] Sync failed', { batchId: data.batchId });
          reset();
        }
      },
      onError: (error: unknown) => {
        Logger.error(
          '[SyncBatch] Failed to check sync status',
          error instanceof Error ? error.message : String(error),
        );
        reset();
      },
      cacheTime: MAX_POLL_DURATION,
      staleTime: 0,
    },
  );

  return {
    triggerSync,
    statusQuery,
    batchId,
    isPollingActive: isPollingActive(),
    reset,
  };
};
